import { ASSETS, EXTERNAL_LINKS } from '../../config/site';
import Accordion from '../ui/Accordion';
import { ArrowIcon } from '../ui/Icons';
import { ButtonLink, Eyebrow, SectionIntro, TextLink } from '../ui/Primitives';

export function PageHero({ eyebrow, title, description, primaryLabel, secondaryLabel, secondaryTo = EXTERNAL_LINKS.meeting, visual, className = '' }) {
  return (
    <section className={`page-hero ${className}`.trim()} aria-labelledby="page-title">
      <div className="page-hero__ambient" aria-hidden="true" />
      <div className="page-hero__copy">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 id="page-title">{title}</h1>
        <p>{description}</p>
        <div className="page-hero__actions">
          <ButtonLink>{primaryLabel}</ButtonLink>
          <TextLink to={secondaryTo} target="_blank" rel="noopener noreferrer">{secondaryLabel}</TextLink>
        </div>
      </div>
      {visual ? <div className="page-hero__visual">{visual}</div> : null}
    </section>
  );
}

export function StepsSection({ eyebrow, title, items, primaryLabel, secondaryLabel }) {
  return (
    <section className="section-shell steps-section">
      <SectionIntro eyebrow={eyebrow} title={title} />
      <div className="steps-grid" data-reveal="stagger">
        {items.map((item, index) => (
          <article className="step-card" key={item.title} style={{ '--stagger': index }}>
            <div className="step-card__top">
              <span>{String(index + 1).padStart(2, '0')}</span>
              <span className="step-card__icon" aria-hidden="true">{index === 0 ? '↗' : index === 1 ? '+' : '✓'}</span>
            </div>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>
      {(primaryLabel || secondaryLabel) ? (
        <div className="section-actions" data-reveal="fade">
          {primaryLabel ? <ButtonLink>{primaryLabel}</ButtonLink> : null}
          {secondaryLabel ? <TextLink to="/preise">{secondaryLabel}</TextLink> : null}
        </div>
      ) : null}
    </section>
  );
}

export function FAQSection({ eyebrow, title, items, contact }) {
  return (
    <section className="section-shell faq-section">
      <div className="faq-section__intro" data-reveal="clip">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2>{title}</h2>
        {contact ? (
          <div className="faq-section__contact">
            <p>{contact.text}</p>
            <TextLink to={contact.href} target="_blank" rel="noopener noreferrer">{contact.label}</TextLink>
            {contact.emailLead && contact.email ? <p className="faq-section__email">{contact.emailLead} <a href={`mailto:${contact.email}`}>{contact.email}</a></p> : null}
          </div>
        ) : null}
      </div>
      <div data-reveal="fade"><Accordion items={items} /></div>
    </section>
  );
}

export function FinalCTA({ eyebrow, title, text, buttonLabel }) {
  return (
    <section className="final-cta" data-reveal="clip">
      <div className="final-cta__noise" aria-hidden="true" />
      <div className="final-cta__rings" aria-hidden="true"><i /><i /><i /></div>
      <div className="final-cta__content">
        <Eyebrow tone="light">{eyebrow}</Eyebrow>
        <h2>{title}</h2>
        <p>{text}</p>
        <ButtonLink variant="light">{buttonLabel}</ButtonLink>
        <div className="final-cta__progress" aria-hidden="true"><span /><i /><span /><i /><span /></div>
      </div>
      <div className="final-cta__badge" aria-hidden="true">
        <span>03:00</span>
        <strong>✓</strong>
        <small>YourApplicant</small>
      </div>
    </section>
  );
}

export function MediaFeature({ eyebrow, title, text, buttonLabel, children, reverse = false, className = '' }) {
  return (
    <section className={`media-feature ${reverse ? 'media-feature--reverse' : ''} ${className}`.trim()}>
      <div className="media-feature__copy" data-reveal="clip">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2>{title}</h2>
        <p>{text}</p>
        <ButtonLink>{buttonLabel}</ButtonLink>
      </div>
      <div className="media-feature__visual" data-reveal="image">{children}</div>
    </section>
  );
}

export function DossierPreview({ labels }) {
  return (
    <div className="dossier-preview glass-card">
      <div className="window-bar"><i /><i /><i /><span>app.yourapplicant.ch/dossier</span></div>
      <div className="dossier-preview__head">
        <img src="/assets/optimized/people/4aed114fe6b983d3.avif" alt="Ben" />
        <div><small>{labels.kicker}</small><h3>Ben Keller</h3><p>{labels.traits}</p></div>
        <strong>92%</strong>
      </div>
      <div className="progress-track"><i /></div>
      <div className="dossier-preview__grid">
        {labels.stats.map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>)}
      </div>
    </div>
  );
}

export function MatchPreview({ labels }) {
  return (
    <article className="match-preview glass-card">
      <div className="match-preview__head">
        <span className="mini-logo"><img src={ASSETS.logo} alt="" /></span>
        <div><small>{labels.kicker}</small><h3>{labels.title}</h3></div>
        <span className="match-preview__apply">{labels.apply}<ArrowIcon /></span>
      </div>
      <div className="match-preview__badges"><span>2026</span><span>{labels.match}</span></div>
      <hr />
      <h4>{labels.why}</h4>
      <p>{labels.text}</p>
    </article>
  );
}
